import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ScheduleCalendar from "../../components/ScheduleCalendar/ScheduleCalendar";
import SaturnePage from "../../components/SaturnePage/SaturnePage";
import { useScheduleCalendar } from "../../hooks/useScheduleCalendar";
import { getErrorMessage } from "../../services/ErrorMessage";
import { SupportedActorType } from "../../services/fhir/fhir.types";
import { ScheduleSummary } from "../../services/scheduling/Scheduling.types";
import { createSchedulingServices } from "../../services/scheduling/SchedulingServiceFactory";

const supportedActorTypes: SupportedActorType[] = [
  "patient",
  "practitioner",
  "location",
  "device",
];

function isSupportedActorType(value: string | undefined): value is SupportedActorType {
  return Boolean(value && supportedActorTypes.includes(value as SupportedActorType));
}

export default function ActorCalendarPage() {
  const { t } = useTranslation();
  const { actorType, actorId } = useParams();
  const services = useMemo(createSchedulingServices, []);
  const [title, setTitle] = useState(t("actorCalendar.title"));
  const [subtitle, setSubtitle] = useState(t("actorCalendar.loadingSubtitle"));
  const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [schedulesError, setSchedulesError] = useState<string>();
  const calendar = useScheduleCalendar(services.calendarService, schedules);

  const loadActorCalendar = useCallback(async () => {
    setSchedulesLoading(true);
    setSchedulesError(undefined);

    if (!actorId || !isSupportedActorType(actorType)) {
      setSchedules([]);
      setSchedulesError(t("actorCalendar.invalidActor"));
      setSchedulesLoading(false);
      return;
    }

    try {
      const actor = await services.actorService.getById(actorType, actorId);
      if (!actor) {
        setSchedules([]);
        setSchedulesError(t("actorCalendar.notFound"));
        return;
      }

      const actorCalendar = await services.actorCalendarService.getForActors([actor]);
      setTitle(actor.display);
      setSubtitle(
        t("actorCalendar.subtitle", {
          type: t(`calendar.actorTypes.${actor.type}`),
        }),
      );
      setSchedules(actorCalendar.schedules);
    } catch (error) {
      setSchedules([]);
      setSchedulesError(getErrorMessage(error));
    } finally {
      setSchedulesLoading(false);
    }
  }, [actorId, actorType, services, t]);

  useEffect(() => {
    void loadActorCalendar();
  }, [loadActorCalendar]);

  return (
    <SaturnePage loading={false} needsLogin wide>
      <ScheduleCalendar
        title={title}
        subtitle={subtitle}
        schedules={schedules}
        selectedScheduleIds={calendar.selectedScheduleIds}
        events={calendar.events}
        anchorDate={calendar.anchorDate}
        view={calendar.view}
        schedulesLoading={schedulesLoading}
        eventsLoading={calendar.eventsLoading}
        schedulesError={schedulesError}
        eventsError={calendar.eventsError}
        onSelectedScheduleIdsChange={calendar.setSelectedScheduleIds}
        onAnchorDateChange={calendar.setAnchorDate}
        onViewChange={calendar.setView}
        onRetrySchedules={() => void loadActorCalendar()}
        onRetryEvents={calendar.retryEvents}
      />
    </SaturnePage>
  );
}
